import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType, LoginProviderEnum} from '../../../../shared/entity_datas/EntityDatas.ts'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct.ts'
import {PaymentMethod} from '../../../../shared/store/BaseColonistStoreProduct.ts'
import {type UserLoginState} from '../../../../shared/UserLoginState.ts'
import {ClientUserState} from '../ClientUserState.ts'
import {clientI18n} from '../ClientI18n.ts'
import {tabCommunication} from '../EntryShared.ts'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints.ts'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper.ts'
import {TabCommunicationEvent} from '../tab_communication/TabCommunication.ts'
import {UIPopupController} from '../ui_popup/UIPopupController.ts'
import {ViewPopupInput} from '../ui_popup/ViewPopupInput.ts'
import {UIProfileSettingsRegionChangeController} from './UIProfileSettingsRegionChangeController.ts'
import {StorageWrapper} from '../StorageWrapper.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {ViewPopupDiscordSettings} from '../ui_popup/ViewPopupDiscordSettings.ts'
import {getHomepageUrl, navigateToPage} from '../ui_utils/ClientUtils.ts'
import {setElementOnClick, createImgIn, createLinkIn, createParagraphIn, setElementInnerHTML, createDivIn, createHeaderIn} from '../ui_utils/DomUtils.ts'
import {CA} from '../analytics/Analytics.ts'
import {audioManager, AudioType} from '../Sound.ts'
import {UICheckoutAjaxEndPoints} from '../ui_checkout/UICheckoutAjaxEndPoints.ts'
import {getNestedTranslation, type TranslationObject} from '@colonist/i18n'
import {ViewPopup} from '../ui_popup/ViewPopup.ts'

export class UIProfileSettingsController {
    private readonly membershipStatusContainer: HTMLDivElement
    private readonly deleteAccountButton: HTMLDivElement
    private readonly logoutButton: HTMLDivElement | null

    private readonly googleEmail: HTMLParagraphElement
    private readonly discordEmail: HTMLParagraphElement
    private readonly appleEmail: HTMLParagraphElement

    private readonly googleButton: HTMLAnchorElement
    private readonly discordButton: HTMLAnchorElement
    private readonly appleButton: HTMLAnchorElement

    private currentUserLoginState: UserLoginState[]

    constructor() {
        this.membershipStatusContainer = document.getElementById('membership-status-container') as HTMLDivElement
        this.deleteAccountButton = document.getElementById('page-profile-settings-delete-account') as HTMLDivElement
        this.logoutButton = document.getElementById('page-profile-settings-logout') as HTMLDivElement | null

        setElementOnClick(this.deleteAccountButton, () => {
            if(UIUtilsEjsVariables.isUserOnDiscord) {
                new ViewPopupDiscordSettings()
                return
            }
            UIProfileSettingsController.showDeleteAccountPopup()
        })
        if(this.logoutButton) setElementOnClick(this.logoutButton, () => UIProfileSettingsController.handleLogout())

        this.googleEmail = document.getElementById('page-profile-settings-account-email-google') as HTMLParagraphElement
        this.discordEmail = document.getElementById('page-profile-settings-account-email-discord') as HTMLParagraphElement
        this.appleEmail = document.getElementById('page-profile-settings-account-email-apple') as HTMLParagraphElement

        this.googleButton = document.getElementById('page-profile-settings-account-button-google') as HTMLAnchorElement
        this.discordButton = document.getElementById('page-profile-settings-account-button-discord') as HTMLAnchorElement
        this.appleButton = document.getElementById('page-profile-settings-account-button-apple') as HTMLAnchorElement

        new UIProfileSettingsRegionChangeController()
        this.initMembershipStatus()
        this.initAccountsSection()
    }

    private initMembershipStatus() {
        if(ClientUserState.userState?.items.hasMembership()) {
            this.showMembershipTitleAndIcon()
            return
        }

        this.showNotMemberTextAndBecomeMemberCTA()
    }

    private showMembershipTitleAndIcon() {
        const currentMembership = ClientUserState.userState?.items.getCurrentMembership()
        if(currentMembership == undefined) return
        const currentMembershipProduct = ColonistMembershipProduct.getMembershipProduct(currentMembership.type)

        const textContainer = createDivIn(this.membershipStatusContainer, 'page-profile-settings-account-container', '')
        createImgIn(textContainer, currentMembership.image)

        const membershipTitleText = getNestedTranslation({key: 'strings:storePage.membership.body'}, {
            title: currentMembership.title,
            frequency: currentMembershipProduct.billedAnnually ? {key: 'strings:storePage.membership.frequency.annual'} : {key: 'strings:storePage.membership.frequency.monthly'},
        })
        createHeaderIn(textContainer, membershipTitleText, '', '', 'H3')

        const canManage = (ClientUserState.userState?.items.membershipPaymentMethod == PaymentMethod.Stripe) || (ClientUserState.userState?.items.membershipPaymentMethod == PaymentMethod.Discord)
        if(canManage) {
            const membershipManageButton = createDivIn(this.membershipStatusContainer, 'page-profile-settings-account-button disconnect')
            setElementInnerHTML(membershipManageButton, {key: 'strings:storePage.sections.membership.action.manage'})
            setElementOnClick(membershipManageButton, () => UIProfileSettingsController.handleSettingsClick(() => UIProfileSettingsController.manageMembershipClickAction()))

            this.membershipStatusContainer.appendChild(membershipManageButton)
        }
    }

    private static manageMembershipClickAction() {
        audioManager.play(AudioType.Click)
        CA.sendGoogleAnalyticsEvent(CA.Category.Profile, CA.Label.ClickedProfileManageMembership, ClientUserState.userState?.items.membership)

        void UICheckoutAjaxEndPoints.executeStripeCustomerPortalSession()
    }

    private showNotMemberTextAndBecomeMemberCTA() {
        const notMemberText = createParagraphIn(this.membershipStatusContainer, {key: 'strings:profilePage.settings.membership.dontHaveMemebership'}, '')
        const becomeMemberCTA = createLinkIn(this.membershipStatusContainer, '', {key: 'strings:profilePage.settings.membership.becomeMember'}, 'button_cta_main')
        setElementOnClick(becomeMemberCTA, () => UIProfileSettingsController.becomeMemberClickAction(), true)

        this.membershipStatusContainer.appendChild(notMemberText)
        this.membershipStatusContainer.appendChild(becomeMemberCTA)
    }

    protected static handleSettingsClick(action: () => void) {
        if(UIUtilsEjsVariables.isUserOnDiscord) {
            new ViewPopupDiscordSettings()
            return
        }
        action()
    }

    private static becomeMemberClickAction() {
        audioManager.play(AudioType.Click)
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ClickedStore,
            category: AnalyticEventCategory.Store,
        }
        void CA.sendAnalyticEvent(eventData)

        navigateToPage('/store#membership', undefined, true)
    }

    private initAccountsSection() {
        UIProfileSettingsController.setConnectAccountButtonAction(this.googleButton, UIAPIEndpoints.googleLink())
        UIProfileSettingsController.setConnectAccountButtonAction(this.discordButton, UIAPIEndpoints.discordLink())
        UIProfileSettingsController.setConnectAccountButtonAction(this.appleButton, UIAPIEndpoints.appleLink())
    }

    setUserLoginState(loginState: UserLoginState[]) {
        this.currentUserLoginState = loginState
        this.setAccountEmailsIfAlreadyConnected()
    }

    private setAccountEmailsIfAlreadyConnected() {
        for(const account of this.currentUserLoginState) {
            const emailText: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: account.email}}
            switch(account.providerId) {
                case LoginProviderEnum.Google: {
                    setElementInnerHTML(this.googleEmail, emailText)
                    this.googleEmail.classList.remove('hidden')
                    UIProfileSettingsController.setDisconnectAccountButtonAction(this.googleButton, UIAPIEndpoints.googleUnlink())
                    break
                }
                case LoginProviderEnum.Discord: {
                    setElementInnerHTML(this.discordEmail, emailText)
                    this.discordEmail.classList.remove('hidden')
                    UIProfileSettingsController.setDisconnectAccountButtonAction(this.discordButton, UIAPIEndpoints.discordUnlink())
                    break
                }
                case LoginProviderEnum.Apple: {
                    setElementInnerHTML(this.appleEmail, emailText)
                    this.appleEmail.classList.remove('hidden')
                    UIProfileSettingsController.setDisconnectAccountButtonAction(this.appleButton, UIAPIEndpoints.appleUnlink())
                    break
                }
            }
        }
    }

    private static setConnectAccountButtonAction(element: HTMLSpanElement, apiLink: string) {
        setElementOnClick(element, () => UIProfileSettingsController.handleSettingsClick(() => UIProfileSettingsController.handleConnect(apiLink)))
    }

    private static setDisconnectAccountButtonAction(element: HTMLSpanElement, apiLink: string) {
        setElementOnClick(element, () => UIProfileSettingsController.handleSettingsClick(() => UIProfileSettingsController.handleDisconnect(apiLink)))
        element.classList.add('disconnect')
        setElementInnerHTML(element, {key: 'strings:profilePage.settings.accounts.disconnect'})
    }

    private static handleConnect(apiLink: string) {
        navigateToPage(apiLink)
    }

    private static async handleDisconnect(apiLink: string) {
        UIPopupController.create({key: 'strings:profilePage.removeAccount.title'}, {key: 'strings:profilePage.removeAccount.body'}, async() => {
            const response = await UIAjaxWrapper.getRequestHandler(apiLink, undefined)
            if(response == undefined) return
            location.reload()
        }, () => undefined)
    }

    private static async checkIfUsernameMatchesInput(username: string, inputtedUsername: string) {
        if(username.toLowerCase() != inputtedUsername.toLocaleLowerCase()) {
            UIPopupController.createSimplePopup({key: 'strings:popups.errors.general.title'}, {key: 'strings:profilePage.deleteAccount.usernamesDontMatch'})
            return
        }

        const data: AjaxDataTypes.DeleteUserData = {username: inputtedUsername}
        const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiDeleteProfile(), undefined, data, 'application/json')
        if(response == undefined) return

        StorageWrapper.clearUserState()

        tabCommunication.sendEvent(TabCommunicationEvent.ReloadWebsite)
        navigateToPage('/?account-deleted=1')
    }

    private static showDeleteAccountPopup() {
        if(ClientUserState.userState == undefined) return
        const username = ClientUserState.userState.username
        const popup = new ViewPopupInput({key: 'strings:profilePage.deleteAccount.title'},
            {key: 'strings:profilePage.deleteAccount.body', options: {username: username}},
            (inputtedUsername) => UIProfileSettingsController.checkIfUsernameMatchesInput(username, inputtedUsername),
            () => undefined,
            true)
        popup.body.style.flexDirection = 'column'
        popup.setPlaceholder(clientI18n.t('strings:profilePage.deleteAccount.inputPlaceholder'))
        popup.setLengthLimits(1, 100)
        popup.checkButton.innerText = clientI18n.t('strings:profilePage.deleteAccount.button')
        popup.checkButton.classList.add('btn_danger')
        popup.checkButton.classList.remove('btn_general_check')
        popup.checkButton.style.width = 'initial'
        popup.xButton.style.margin = '0'

        popup.show()
    }

    private static handleLogout() {
        const popup = new ViewPopup({key: 'strings:homePage.header.logoutPopup.title'},
            {key: 'strings:homePage.header.logoutPopup.body'},
            async() => {
                const response = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.logout(), undefined, undefined)
                if(response == undefined) return
                StorageWrapper.setUserBypassedLogin(false)
                StorageWrapper.clearUserState()
                navigateToPage(getHomepageUrl())
            },
            () => undefined, false)
        popup.show()
    }
}
